import { Injectable } from '@angular/core';
import { EVFunctions, ICodeTitle, IGetQuery, IResponse, ISearchResponse2 } from 'ets-fe-ng-sdk';
import {
  IWorkflowApplication,
  IWorkflowApplicationResponse,
  IWorkflowQuestList,
  ISearchWorkflowApplication,
  IWorkflowApplicant,
  IWorkflowApplicationApproval,
} from './application.model';
import { BaseService, BaseWorkflowService } from '../../../../shared/src/lib/services/base.service';
import { FormControl } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { catchError, forkJoin, map, mergeMap, of } from 'rxjs';
import { ISR } from 'projects/shared/src/lib/models/index.model';

/**
 * Service for managing workflow applications.
 * Provides methods for searching, creating, and managing application responses.
 */
@Injectable({
  providedIn: 'root',
})
export class ApplicationService extends BaseWorkflowService {
  protected override baseURL: string = 'v1/workflow/';
  protected naicomUrl: string = 'v1/naicom/workflow/';
  get applicationQuestionFS() {
    return new FormGroup({
      questionNo: new FormControl<string>(''),
      question: new FormControl<string>(''),
      response: new FormControl<string>(''),
      id: new FormControl<number | null>(null),
      responseType: new FormControl<string>(''),
      mandatory: new FormControl<boolean>(false),
      uploadReq: new FormControl<boolean>(false),
      docSubCat: new FormControl<string>(''),
      docKey: new FormControl<string | null>(null),
      file: new FormControl<File | null>(null),
      fileName: new FormControl<string | null>(null),
      ifYesText: new FormControl<string | null>(null),
      ifYesListValue: new FormControl<string | null>(null),
      additionalResponse: new FormControl<string | null>(null),
    });
  }
  /**
   * Search for workflow applications.
   * Default behavior shows applications where Completed = False.
   * @param query - Search query parameters
   * @returns Observable of search results
   */
  search = (query?: ISearchWorkflowApplication) =>
    this.get<ISearchResponse2<IWorkflowApplication>>('application/search', query);

  searchApplicant = (query?: IGetQuery<IWorkflowApplicant>) =>
    this.get<ISearchResponse2<IWorkflowApplicant>>('applicant/search', query).pipe(
      map((res) => {
        res.content?.forEach((x) => this.applicantFullNameHandler(x));
        return res;
      }),
    );

  searchApplicantByName = (applicantName: string) => {
    return this.searchApplicant({ applicantName }).pipe(
      map((res) =>
        res.content.map(
          (x) =>
            <ICodeTitle>{
              code: x.applicantNo,
              title: x.applicantName!,
            },
        ),
      ),
    );
  };

  /**
   * Get a single application by code
   * @param applicationCode - Application code
   * @returns Observable of application
   */
  getByCode = (applicationCode: string) => this.get<IWorkflowApplication>(`application/${applicationCode}`);

  /**
   * Create a new workflow application.
   * Posts to POST api/v1/workflow/application.
   * @param data - Application data
   * @returns Observable of created application with generated applicationCode
   */
  create = (data: Partial<IWorkflowApplication>) => this.post<IWorkflowApplication>(data, 'application');

  /**
   * Update an existing workflow application
   * @param data - Application data with applicationCode
   * @returns Observable of updated application
   */
  update = (data: Partial<IWorkflowApplication>) =>
    this.put<IWorkflowApplication>(data, 'application/' + data.id);

  /**
   * Get application setup by application type.
   * Fetches questSetCode for the given application type.
   * @param applicationType - Application type code
   * @returns Observable of application setup with questSetCode
   */
  getApplicationSetup = (applicationType: string) =>
    this.get<
      ISearchResponse2<{
        questSetCode?: string;
        applicationType?: string;
        description?: string;
      }>
    >('application-setup/search', { applicationType });

  /**
   * Get questions list by quest set code.
   * Fetches questions for user to respond to.
   * @param questSetCode - Question set code from application setup
   * @returns Observable of questions list
   */
  getQuestionsList = (questSetCode: string) =>
    this.get<ISearchResponse2<IWorkflowQuestList>>('quest/list/search', {
      questSetCode,
    }).pipe(
      map((r) => {
        r.content?.forEach((q) => {
          q.responseReq = typeof q.responseReq === 'string' ? q.responseReq === 'true' : q.responseReq;
          q.mandatory = typeof q.mandatory === 'string' ? q.mandatory === 'true' : q.mandatory;
          q.uploadReq = typeof q.uploadReq === 'string' ? q.uploadReq === 'true' : q.uploadReq;
        });
        return r;
      }),
    );

  /**
   * Search for application responses
   * @param applicationCode - Application code to search responses for
   * @returns Observable of application responses
   */
  getApplicationResponses = (applicationCode: string) =>
    this.get<ISearchResponse2<IWorkflowApplicationResponse>>('application-response/search', {
      applicationCode,
    });

  /**
   * Search for application responses
   * @param params - Search parameters including applicationCode and returnCode
   * @returns Observable of application responses
   */
  getApplicationResponsesByParams = (params: { applicationCode?: string; returnCode?: string }) =>
    this.get<ISearchResponse2<IWorkflowApplicationResponse>>('application-response/search', params);

  /**
   * Submit application responses.
   * Posts to POST workflow/application-response.
   * @param responses - Array of application responses
   * @returns Observable of submitted responses
   */
  submitApplicationResponses = (responses: IWorkflowApplicationResponse[]) =>
    this.post<IWorkflowApplicationResponse[]>(responses, 'application-response');
  deleteApplicationResponses = (responses: { id?: number }[]) =>
    forkJoin(responses.map((r) => (r.id ? this.delete(`application-response/${r.id}`) : of(null))));

  /**
   * Check if applicant exists by email.
   * GETs api/v1/workflow/exist/applicant/email
   * @param email - Applicant email address
   * @returns Observable of applicant data if exists, null otherwise
   */
  checkApplicantByEmail = (email: string) => this.checkApplicantUniqueness('EMAIL', email);
  checkApplicantUniqueness = (applicantCategoryEnum: 'EMAIL' | 'NIN' | 'PHONE_NO' | 'BVN', value: string) =>
    this.get<{ applicantNo: string; unique: boolean }>(`exist/applicant`, {
      applicantCategoryEnum,
      value,
    }).pipe(
      mergeMap((r) =>
        !r.applicantNo
          ? of({ ...r, applicant: null })
          : this.searchApplicant({ applicantNo: r.applicantNo }).pipe(
              map((sr) => ({ ...r, applicant: sr.content[0] })),
            ),
      ),
    );

  private applicantFullNameHandler(data: Partial<IWorkflowApplicant>) {
    data.applicantName =
      data.applicantName ??
      EVFunctions.strConcatenator2(
        [data.applicantFirstName!, data.applicantMiddleName!, data.applicantSurname!],
        ' ',
      );
  }
  /**
   * Create a new applicant.
   * POSTs to api/v1/workflow/applicant
   * @param data - Applicant data
   * @returns Observable of created applicant with generated applicantNo
   */
  createApplicant = (data: Partial<IWorkflowApplicant>) => {
    this.applicantFullNameHandler(data);
    return this.post<IWorkflowApplicant>(data, 'applicant');
  };

  /**
   * Update an existing applicant.
   * PUTs to api/v1/workflow/applicant/{id}
   * @param data - Applicant data with id
   * @returns Observable of updated applicant
   */
  updateApplicant = (data: Partial<IWorkflowApplicant> & { id: number }) => {
    this.applicantFullNameHandler(data);
    return this.put<IWorkflowApplicant>(data, `applicant/${data.id}`);
  };

  /**
   * Delete an applicant.
   * DELETEs api/v1/workflow/applicant/{id}
   * @param id - Applicant ID
   * @returns Observable of delete response
   */
  deleteApplicant = (id: number) => this.delete(`applicant/${id}`);

  /**
   * Search for application feedback.
   * @param applicationCode - Application code to search feedback for
   * @returns Observable of application feedback
   */
  searchApplicationFeedback = (params: { applicationCode: string; pendingResponse?: boolean }) =>
    this.get<ISearchResponse2<any>>('application/feedback/search', params);

  /**
   * Create application feedback.
   * @param feedback - Feedback data
   * @returns Observable of created feedback
   */
  createFeedback = (feedback: { applicationCode: string; regulatorFeedback: string; feedbackBy: string }) =>
    this.post<any>(feedback, 'application/feedback');

  /**
   * Respond to application feedback.
   * @param feedbackId - Feedback ID
   * @param response - Response data
   * @returns Observable of feedback response
   */
  respondToFeedback = (feedbackId: number, response: { response: string; responseBy: string }) =>
    this.post<any>(
      null,
      `application/feedback/response${this.apiS.getRequestParse({
        workflowApplicationFeedbackId: feedbackId,
        response: response.response,
        responseBy: response.responseBy,
      })}`,
    ).pipe(catchError((err) => of(null)));

  /**
   * Search for application approvals.
   * @param params - Search parameters including applicationCode
   * @returns Observable of approval search results
   */
  searchApprovals = (params: { applicationCode: string; auth?: boolean }) =>
    this.get<ISearchResponse2<IWorkflowApplicationApproval>>('application/approval/search', params);

  /**
   * Update an existing application approval.
   * @param data - Approval data with id
   * @returns Observable of updated approval
   */
  updateApproval = (id: number, data: IWorkflowApplicationApproval) =>
    this.put<IWorkflowApplicationApproval>(data, `application/approval/${id}`);

  searchNarration = (query: { applicationCode: string }) =>
    this.get<ISR<any>>(`application/narration/history/search`, query);

  addNarration = (data: { applicationCode: string; narration: string }) =>
    this.post<any>(data, 'application/narration/history');

  updateNarration = (id: number, data: { narration: string }) =>
    this.put<any>(data, `application/narration/history/${id}`);

  deleteNarration = (id: number) => this.delete<any>(`application/narration/history/${id}`);

  setApproval = (data: { applicationCode: string }) =>
    this.post<any>(data, `set/approval/${data.applicationCode}`);

  submitApplication = (applicationCode: string, submittedBy: string) =>
    this.apiS.post<IResponse>(`${this.naicomUrl}submit/application`, {
      applicationCode,
      submittedBy,
    });

  withdrawApplication = (data: { applicationCode: string; cancelReason: string; cancelledBy: string }) =>
    this.post<IResponse>(data, `withdraw/application`);
}
